import React, { useState, useEffect } from 'react';
import { BsFillExclamationTriangleFill } from 'react-icons/bs';
import { useNavigate } from 'react-router-dom';
import Cookies from 'js-cookie';

const BQ_BASE_URL = process.env.REACT_APP_BQ_BASE_URL;

const Booking = () => {
  const [bookingid, setBookingId] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [showPopup, setShowPopup] = useState(false);
  const [popupMessage, setPopupMessage] = useState('');
  const [errors, setErrors] = useState({ bookingid: '', lastName: '' });
  const [isLoading, setIsLoading] = useState(false);
  const [showDisclaimer, setShowDisclaimer] = useState(false);
  const [bookingData, setBookingData] = useState(null);
  const [earlyCheckinData, setEarlyCheckinData] = useState(null);

  const navigate = useNavigate();

  useEffect(() => {
    // Fetch guest data if logged in (only for email)
    const fetchGuestEmail = async () => {
      const guestData = await getGuestData();
      if (guestData && guestData.email) {
        setEmail(guestData.email);
      }
    };

    fetchGuestEmail();
  }, []);

  const getGuestIdFromToken = (token) => {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload.sub;
    } catch (e) {
      console.error('Error parsing token:', e);
      return null;
    }
  };

  const decryptUserData = async (encryptedData) => {
    try {
      if (encryptedData.email && encryptedData.email.length < 50) {
        return encryptedData;
      }

      const accessToken = Cookies.get('access_token');
      if (!accessToken) {
        throw new Error('No access token found');
      }

      const response = await fetch(`${BQ_BASE_URL}/bq/api/decrypt-fields`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          encrypted_fields: {
            email: encryptedData.email,
            phone_number: encryptedData.phone_number,
            country_code: encryptedData.country_code,
          },
        }),
      });

      if (response.status === 401) {
        return encryptedData;
      }

      if (!response.ok) {
        throw new Error(`Decryption failed: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Decryption error:', error);
      return encryptedData;
    }
  };

  const getGuestData = async () => {
    const accessToken = Cookies.get('access_token');
    if (!accessToken) return null;

    try {
      const guestId = Cookies.get('guest_id') || getGuestIdFromToken(accessToken);
      if (!guestId) return null;

      const profileResponse = await fetch(`${BQ_BASE_URL}/bq/api/profile?guest_id=${guestId}`, {
        method: 'GET',
        headers: {
          Accept: 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
      });

      if (!profileResponse.ok) {
        throw new Error(`Profile API Error: ${profileResponse.statusText}`);
      }

      const profileData = await profileResponse.json();
      const isDecrypted = profileData.emailid && profileData.emailid.length < 50;

      if (isDecrypted) {
        return {
          firstname: profileData.firstname || '',
          lastname: profileData.lastname || '',
          email: profileData.emailid || '',
        };
      }

      const decryptedFields = await decryptUserData({
        email: profileData.emailid,
        phone_number: profileData.phonenumber,
        country_code: profileData.countrycode,
      });

      return {
        firstname: profileData.firstname || '',
        lastname: profileData.lastname || '',
        email: decryptedFields.email || profileData.emailid || '',
      };
    } catch (error) {
      console.error('Error fetching guest data:', error);
      return null;
    }
  };

  const handleBookingIdChange = (e) => {
    const value = e.target.value;
    if (/^\d*$/.test(value)) {
      setBookingId(value);
      setErrors((prev) => ({
        ...prev,
        bookingid: value.trim() ? '' : 'Reservation number is required.',
      }));
    } else {
      setErrors((prev) => ({
        ...prev,
        bookingid: 'Reservation number must contain only digits.',
      }));
    }
  };

  const handleLastNameChange = (e) => {
    const value = e.target.value;
    if (/^[a-zA-Z\s'-]*$/.test(value)) {
      setLastName(value);
      setErrors((prev) => ({
        ...prev,
        lastName: value.trim() ? '' : 'Last name is required.',
      }));
    } else {
      setErrors((prev) => ({
        ...prev,
        lastName: 'Last name should contain only alphabets.',
      }));
    }
  };

  const validateFields = () => {
    let valid = true;
    const newErrors = { bookingid: '', lastName: '' };

    if (!bookingid.trim()) {
      newErrors.bookingid = 'Reservation number is required.';
      valid = false;
    } else if (!/^\d+$/.test(bookingid)) {
      newErrors.bookingid = 'Reservation number must contain only digits.';
      valid = false;
    } else if (bookingid.length < 10) {
      newErrors.bookingid = 'Reservation number must be at least 10 digits.';
      valid = false;
    }

    if (!lastName.trim()) {
      newErrors.lastName = 'Last name is required.';
      valid = false;
    } else if (!/^[a-zA-Z\s'-]+$/.test(lastName)) {
      newErrors.lastName = 'Last name should contain only alphabets.';
      valid = false;
    }

    setErrors(newErrors);
    return valid;
  };

  const shouldShowDisclaimer = (checkInDate, earlyCheckinInfo) => {
    const now = new Date();
    const checkInDateTime = new Date(checkInDate);

    // Set check-in time to 11:00 AM
    checkInDateTime.setHours(11, 0, 0, 0);

    // Calculate 2 hours prior to check-in time (11:00 AM)
    const twoHoursPrior = new Date(checkInDateTime);
    twoHoursPrior.setHours(twoHoursPrior.getHours() - 2);

    // If current time is before the 2 hours prior to check-in, show disclaimer
    return now < twoHoursPrior && earlyCheckinInfo && earlyCheckinInfo.allowed;
  };

  const formatErrorMessage = (errorData) => {
    if (!errorData) return 'An error occurred. Please try again.';

    if (errorData.detail && Array.isArray(errorData.detail)) {
      const detailItem = errorData.detail.find(
        (item) => item.loc && item.loc.includes('order_id') && item.msg
      );
      if (detailItem) {
        return detailItem.msg;
      }
    }

    if (errorData.detail) {
      const detail = errorData.detail;

      // Handle the specific email mismatch case
      if (
        detail.message === "We couldn't verify your reservation details" &&
        detail.hint &&
        detail.action_required
      ) {
        return `${detail.message}\n\n${detail.action_required}\n\n${detail.hint}`;
      }

      if (
        detail.message === 'Check-in not available' &&
        detail.issues?.[0]?.message === 'Booking expired'
      ) {
        const expiryDate = new Date(detail.issues[0].expiry_date).toLocaleDateString('en-US', {
          weekday: 'long',
          year: 'numeric',
          month: 'long',
          day: 'numeric',
        });
        return `Your booking expired on ${expiryDate}.\n\nPlease contact support for assistance.`;
      }

      if (detail.message === 'Check-in not available today' && detail.issues?.[0]?.checkin_date) {
        const checkinDate = new Date(detail.issues[0].checkin_date).toLocaleDateString('en-US', {
          weekday: 'long',
          year: 'numeric',
          month: 'long',
          day: 'numeric',
        });
        return `Check-in opens on ${checkinDate}.\n\n${detail.issues[0].message}`;
      }

      if (typeof detail === 'object') {
        if (detail.friendly_message) {
          return `${detail.friendly_message}\n${detail.details || ''}`;
        }
        if (detail.message && detail.issues?.[0]?.message) {
          return `${detail.message}\n\n${detail.issues[0].message}`;
        }
        if (detail.message) {
          return detail.message;
        }
      }

      if (typeof detail === 'string') {
        return detail;
      }
    }

    return errorData.message || 'An error occurred. Please try again.';
  };

  const handleProceed = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    if (!validateFields()) {
      setIsLoading(false);
      return;
    }

    try {
      const requestBody = {
        order_id: bookingid,
        last_name: lastName,
      };

      if (email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        requestBody.email = email;
      }

      const response = await fetch(`${BQ_BASE_URL}/bq/api/pre-checkin/`, {
        method: 'POST',
        headers: {
          accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });

      if (response.ok) {
        const data = await response.json();
        setBookingData(data);

        if (data.success && data.bookings && data.bookings.length > 0) {
          const booking = data.bookings[0];
          const status = booking.status_message;

          // Handle "Already checked in" status
          if (status === 'Already checked in') {
            proceedToNextStep(data);
            return;
          }

          // Check if we need to show the early check-in disclaimer
          if (shouldShowDisclaimer(booking.checkin_date, booking.early_checkin)) {
            setEarlyCheckinData(booking.early_checkin);
            setShowDisclaimer(true);
          } else {
            proceedToNextStep(data);
          }
        } else {
          setPopupMessage(data.message || 'Booking not found.');
          setShowPopup(true);
        }
      } else {
        const errorData = await response.json();
        const formattedMessage = formatErrorMessage(errorData);
        setPopupMessage(formattedMessage);
        setShowPopup(true);
      }
    } catch (error) {
      console.error('Error:', error);
      setPopupMessage('An error occurred. Please try again.');
      setShowPopup(true);
    } finally {
      setIsLoading(false);
    }
  };

  const proceedToNextStep = (data) => {
    const bookingDetails = {
      ...data,
      primaryBooking: data.bookings[0],
    };

    localStorage.setItem('bookingDetails', JSON.stringify(bookingDetails));
    sessionStorage.setItem('bookingDetails', JSON.stringify(bookingDetails));

    const status = data.bookings[0].status_message;

    if (status === 'Ready for check-in') {
      navigate('/checkin-details');
    } else if (status === 'Already checked in') {
      navigate('/reservation/options/booking/welcome');
    } else {
      setPopupMessage(status || 'Unexpected booking status.');
      setShowPopup(true);
    }
  };

  const proceedWithCheckIn = () => {
    setShowDisclaimer(false);
    if (bookingData) {
      proceedToNextStep(bookingData);
    }
  };

  const getHotelContactInfo = () => {
    try {
      const hotelPropertyCache = localStorage.getItem('hotelPropertyCache');
      if (hotelPropertyCache) {
        const { data } = JSON.parse(hotelPropertyCache);
        return {
          phone: data.phone,
          email: data.email,
          propertyName: data.propertyname || data.name,
        };
      }
    } catch (error) {
      console.error('Error parsing hotel property cache:', error);
    }
    return {
      phone: 'N/A',
      email: 'N/A',
      propertyName: 'the hotel',
    };
  };

  const { phone, email: hotelEmail, propertyName } = getHotelContactInfo();

  return (
    <div className="flex items-center justify-center w-full px-4 py-8">
      <div className="w-full max-w-md">
        <div className="text-center">
          <h2 className="text-3xl font-semibold text-black"> </h2>
          <p className="text-md text-gray-500 mb-6"></p>
        </div>
        <form onSubmit={handleProceed}>
          <div className="mb-4">
            <label htmlFor="bookingId" className="block text-md font-medium text-black mb-1">
              Reservation Number
            </label>
            <input
              type="text"
              id="bookingId"
              value={bookingid}
              onChange={handleBookingIdChange}
              onBlur={() => {
                if (!bookingid.trim()) {
                  setErrors((prev) => ({
                    ...prev,
                    bookingid: 'Reservation number is required.',
                  }));
                } else if (bookingid.length < 10) {
                  setErrors((prev) => ({
                    ...prev,
                    bookingid: 'Reservation number must be at least 10 digits.',
                  }));
                }
              }}
              className={`w-full p-2.5 border ${
                errors.bookingid ? 'border-red-500' : 'border-gray-300'
              } rounded-md placeholder-gray-400`}
              placeholder="Enter your Reservation number"
              required
            />
            <div className="h-2">
              {errors.bookingid && <p className="text-red-500 text-xs mt-1">{errors.bookingid}</p>}
            </div>
          </div>

          <div className="mb-6">
            <label htmlFor="lastName" className="block text-md font-medium text-black mb-1">
              Last Name
            </label>
            <input
              type="text"
              id="lastName"
              value={lastName}
              onChange={handleLastNameChange}
              onBlur={() => {
                if (!lastName.trim()) {
                  setErrors((prev) => ({
                    ...prev,
                    lastName: 'Last name is required.',
                  }));
                }
              }}
              className={`w-full p-2.5 border ${
                errors.lastName ? 'border-red-500' : 'border-gray-300'
              } rounded-md placeholder-gray-400`}
              placeholder="Enter your Last name"
              required
            />
            <div className="h-2">
              {errors.lastName && <p className="text-red-500 text-xs mt-1">{errors.lastName}</p>}
            </div>
          </div>

          <button
            type="submit"
            className="w-full py-2.5 font-poppins bg-black text-white rounded-md hover:bg-gray-800 font-semibold text-base transition-colors"
            disabled={isLoading}
          >
            {isLoading ? 'Searching...' : 'Search Reservation'}
          </button>
        </form>

        {showPopup && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm">
            <div className="relative bg-white rounded-lg shadow-lg max-w-[550px] w-full p-6 mx-4">
              <button
                onClick={() => setShowPopup(false)}
                className="absolute top-4 right-4 text-gray-500 hover:text-gray-700"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-6 w-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>

              <div className="text-center pt-2">
                <BsFillExclamationTriangleFill className="text-yellow-400 text-4xl mx-auto mb-3" />
                <h2 className="text-gray-900 text-lg font-semibold mb-2">Unable to Proceed</h2>
                <p className="text-red-600 text-md font-medium mb-3 whitespace-pre-line">
                  {popupMessage}
                </p>
                <p className="text-gray-700 text-sm mb-4 mt-2">
                  Please check your Reservation No and Last Name. In case you are not able to search
                  your reservation please contact our reception asistance support team at{' '}
                  <a href={`mailto:${hotelEmail}`} className="text-blue-600 hover:underline">
                    {hotelEmail}
                  </a>{' '}
                  or call us at{' '}
                  <a href={`tel:${phone}`} className="text-blue-600 hover:underline">
                    {phone}
                  </a>
                </p>

                <button
                  onClick={() => setShowPopup(false)}
                  className="mr-4 px-5 py-2 bg-black text-white rounded-md font-medium"
                >
                  Try Again
                </button>
                <button
                  onClick={() => {
                    setShowPopup(false);
                    navigate('/create-user-later');
                  }}
                  className=" px-5 py-2 bg-black text-white rounded-md font-medium"
                >
                  Complete Profile
                </button>
              </div>
            </div>
          </div>
        )}

        {showDisclaimer && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg max-w-lg w-full p-6">
              <h2 className="text-xl font-bold mb-4">Early Check-In Notice</h2>
              <p className="mb-4">
                You are attempting to check in before the standard check-in time of{' '}
                <strong>11:00 AM</strong>. Early check-in will be charged{' '}
                <strong>₹{earlyCheckinData?.extra_charge}</strong> and is subject to room
                availability.
              </p>

              <p className="mb-6 font-semibold">
                Would you like to proceed with the early check-in?
              </p>
              <div className="flex justify-end space-x-4">
                <button
                  onClick={() => setShowDisclaimer(false)}
                  disabled={isLoading}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-100 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={proceedWithCheckIn}
                  disabled={isLoading}
                  className="px-4 py-2 bg-black text-white rounded-md hover:bg-gray-800 disabled:opacity-50"
                >
                  {isLoading ? 'Processing...' : 'Proceed'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Booking;

// import React, { useState, useEffect } from "react";
// import { BsFillExclamationTriangleFill } from "react-icons/bs";
// import { useNavigate } from "react-router-dom";
// import Cookies from "js-cookie";

// const BQ_BASE_URL = process.env.REACT_APP_BQ_BASE_URL;

// const Booking = () => {
//   const [bookingid, setBookingId] = useState("");
//   const [lastName, setLastName] = useState("");
//   const [email, setEmail] = useState("");
//   const [showPopup, setShowPopup] = useState(false);
//   const [popupMessage, setPopupMessage] = useState("");
//   const [errors, setErrors] = useState({ bookingid: "", lastName: "" });
//   const [isLoading, setIsLoading] = useState(false);

//   const navigate = useNavigate();

//   useEffect(() => {
//     // Fetch guest data if logged in (only for email)
//     const fetchGuestEmail = async () => {
//       const guestData = await getGuestData();
//       if (guestData && guestData.email) {
//         setEmail(guestData.email);
//       }
//     };

//     fetchGuestEmail();
//   }, []);

//   const getGuestIdFromToken = (token) => {
//     try {
//       const payload = JSON.parse(atob(token.split(".")[1]));
//       return payload.sub;
//     } catch (e) {
//       console.error("Error parsing token:", e);
//       return null;
//     }
//   };

//   const decryptUserData = async (encryptedData) => {
//     try {
//       if (encryptedData.email && encryptedData.email.length < 50) {
//         return encryptedData;
//       }

//       const accessToken = Cookies.get("access_token");
//       if (!accessToken) {
//         throw new Error("No access token found");
//       }

//       const response = await fetch(`${BQ_BASE_URL}/bq/api/decrypt-fields`, {
//         method: "POST",
//         headers: {
//           "Content-Type": "application/json",
//           Authorization: `Bearer ${accessToken}`,
//         },
//         body: JSON.stringify({
//           encrypted_fields: {
//             email: encryptedData.email,
//             phone_number: encryptedData.phone_number,
//             country_code: encryptedData.country_code,
//           },
//         }),
//       });

//       if (response.status === 401) {
//         return encryptedData;
//       }

//       if (!response.ok) {
//         throw new Error(`Decryption failed: ${response.statusText}`);
//       }

//       return await response.json();
//     } catch (error) {
//       console.error("Decryption error:", error);
//       return encryptedData;
//     }
//   };

//   const getGuestData = async () => {
//     const accessToken = Cookies.get("access_token");
//     if (!accessToken) return null;

//     try {
//       const guestId =
//         Cookies.get("guest_id") || getGuestIdFromToken(accessToken);
//       if (!guestId) return null;

//       const profileResponse = await fetch(
//         `${BQ_BASE_URL}/bq/api/profile?guest_id=${guestId}`,
//         {
//           method: "GET",
//           headers: {
//             Accept: "application/json",
//             Authorization: `Bearer ${accessToken}`,
//           },
//         }
//       );

//       if (!profileResponse.ok) {
//         throw new Error(`Profile API Error: ${profileResponse.statusText}`);
//       }

//       const profileData = await profileResponse.json();
//       const isDecrypted =
//         profileData.emailid && profileData.emailid.length < 50;

//       if (isDecrypted) {
//         return {
//           firstname: profileData.firstname || "",
//           lastname: profileData.lastname || "",
//           email: profileData.emailid || "",
//         };
//       }

//       const decryptedFields = await decryptUserData({
//         email: profileData.emailid,
//         phone_number: profileData.phonenumber,
//         country_code: profileData.countrycode,
//       });

//       return {
//         firstname: profileData.firstname || "",
//         lastname: profileData.lastname || "",
//         email: decryptedFields.email || profileData.emailid || "",
//       };
//     } catch (error) {
//       console.error("Error fetching guest data:", error);
//       return null;
//     }
//   };

//   const handleBookingIdChange = (e) => {
//     const value = e.target.value;
//     if (/^\d*$/.test(value)) {
//       setBookingId(value);
//       setErrors((prev) => ({
//         ...prev,
//         bookingid: value.trim() ? "" : "Reservation number is required.",
//       }));
//     } else {
//       setErrors((prev) => ({
//         ...prev,
//         bookingid: "Reservation number must contain only digits.",
//       }));
//     }
//   };

//   const handleLastNameChange = (e) => {
//     const value = e.target.value;
//     if (/^[a-zA-Z\s'-]*$/.test(value)) {
//       setLastName(value);
//       setErrors((prev) => ({
//         ...prev,
//         lastName: value.trim() ? "" : "Last name is required.",
//       }));
//     } else {
//       setErrors((prev) => ({
//         ...prev,
//         lastName: "Last name should contain only alphabets.",
//       }));
//     }
//   };

//   const validateFields = () => {
//     let valid = true;
//     const newErrors = { bookingid: "", lastName: "" };

//     if (!bookingid.trim()) {
//       newErrors.bookingid = "Reservation number is required.";
//       valid = false;
//     } else if (!/^\d+$/.test(bookingid)) {
//       newErrors.bookingid = "Reservation number must contain only digits.";
//       valid = false;
//     } else if (bookingid.length < 10) {
//       newErrors.bookingid = "Reservation number must be at least 10 digits.";
//       valid = false;
//     }

//     if (!lastName.trim()) {
//       newErrors.lastName = "Last name is required.";
//       valid = false;
//     } else if (!/^[a-zA-Z\s'-]+$/.test(lastName)) {
//       newErrors.lastName = "Last name should contain only alphabets.";
//       valid = false;
//     }

//     setErrors(newErrors);
//     return valid;
//   };

//   const formatErrorMessage = (errorData) => {
//     if (!errorData) return "An error occurred. Please try again.";

//     if (errorData.detail && Array.isArray(errorData.detail)) {
//       const detailItem = errorData.detail.find(
//         (item) => item.loc && item.loc.includes("order_id") && item.msg
//       );
//       if (detailItem) {
//         return detailItem.msg;
//       }
//     }

//     if (errorData.detail) {
//       const detail = errorData.detail;

//       // Handle the specific email mismatch case
//       if (
//         detail.message === "We couldn't verify your reservation details" &&
//         detail.hint &&
//         detail.action_required
//       ) {
//         return `${detail.message}\n\n${detail.action_required}\n\n${detail.hint}`;
//       }

//       if (
//         detail.message === "Check-in not available" &&
//         detail.issues?.[0]?.message === "Booking expired"
//       ) {
//         const expiryDate = new Date(
//           detail.issues[0].expiry_date
//         ).toLocaleDateString("en-US", {
//           weekday: "long",
//           year: "numeric",
//           month: "long",
//           day: "numeric",
//         });
//         return `Your booking expired on ${expiryDate}.\n\nPlease contact support for assistance.`;
//       }

//       if (
//         detail.message === "Check-in not available today" &&
//         detail.issues?.[0]?.checkin_date
//       ) {
//         const checkinDate = new Date(
//           detail.issues[0].checkin_date
//         ).toLocaleDateString("en-US", {
//           weekday: "long",
//           year: "numeric",
//           month: "long",
//           day: "numeric",
//         });
//         return `Check-in opens on ${checkinDate}.\n\n${detail.issues[0].message}`;
//       }

//       if (typeof detail === "object") {
//         if (detail.friendly_message) {
//           return `${detail.friendly_message}\n${detail.details || ""}`;
//         }
//         if (detail.message && detail.issues?.[0]?.message) {
//           return `${detail.message}\n\n${detail.issues[0].message}`;
//         }
//         if (detail.message) {
//           return detail.message;
//         }
//       }

//       if (typeof detail === "string") {
//         return detail;
//       }
//     }

//     return errorData.message || "An error occurred. Please try again.";
//   };

//   const handleProceed = async (e) => {
//     e.preventDefault();
//     setIsLoading(true);

//     if (!validateFields()) {
//       setIsLoading(false);
//       return;
//     }

//     try {
//       const requestBody = {
//         order_id: bookingid,
//         last_name: lastName,
//       };

//       if (email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
//         requestBody.email = email;
//       }

//       const response = await fetch(`${BQ_BASE_URL}/bq/api/pre-checkin/`, {
//         method: "POST",
//         headers: {
//           accept: "application/json",
//           "Content-Type": "application/json",
//         },
//         body: JSON.stringify(requestBody),
//       });

//       if (response.ok) {
//         const data = await response.json();

//         if (data.success && data.bookings && data.bookings.length > 0) {
//           const bookingDetails = {
//             ...data,
//             primaryBooking: data.bookings[0],
//           };

//           localStorage.setItem(
//             "bookingDetails",
//             JSON.stringify(bookingDetails)
//           );
//           sessionStorage.setItem(
//             "bookingDetails",
//             JSON.stringify(bookingDetails)
//           );

//           const status = data.bookings[0].status_message;

//           if (status === "Ready for check-in") {
//             navigate("/checkin-details");
//           } else if (status === "Already checked in") {
//             navigate("/reservation/options/booking/welcome");
//           } else {
//             setPopupMessage(status || "Unexpected booking status.");
//             setShowPopup(true);
//           }
//         } else {
//           setPopupMessage(data.message || "Booking not found.");
//           setShowPopup(true);
//         }
//       } else {
//         const errorData = await response.json();
//         const formattedMessage = formatErrorMessage(errorData);
//         setPopupMessage(formattedMessage);
//         setShowPopup(true);
//       }
//     } catch (error) {
//       console.error("Error:", error);
//       setPopupMessage("An error occurred. Please try again.");
//       setShowPopup(true);
//     } finally {
//       setIsLoading(false);
//     }
//   };

//   const getHotelContactInfo = () => {
//     try {
//       const hotelPropertyCache = localStorage.getItem("hotelPropertyCache");
//       if (hotelPropertyCache) {
//         const { data } = JSON.parse(hotelPropertyCache);
//         return {
//           phone: data.phone,
//           email: data.email,
//           propertyName: data.propertyname || data.name,
//         };
//       }
//     } catch (error) {
//       console.error("Error parsing hotel property cache:", error);
//     }
//     return {
//       phone: "N/A",
//       email: "N/A",
//       propertyName: "the hotel",
//     };
//   };

//   const { phone, email: hotelEmail, propertyName } = getHotelContactInfo();

//   return (
//     <div className="flex items-center justify-center w-full px-4 py-8">
//       <div className="w-full max-w-md">
//         <div className="text-center">
//           <h2 className="text-3xl font-semibold text-black"> </h2>
//           <p className="text-md text-gray-500 mb-6"></p>
//         </div>
//         <form onSubmit={handleProceed}>
//           <div className="mb-4">
//             <label
//               htmlFor="bookingId"
//               className="block text-md font-medium text-black mb-1"
//             >
//               Reservation Number
//             </label>
//             <input
//               type="text"
//               id="bookingId"
//               value={bookingid}
//               onChange={handleBookingIdChange}
//               onBlur={() => {
//                 if (!bookingid.trim()) {
//                   setErrors((prev) => ({
//                     ...prev,
//                     bookingid: "Reservation number is required.",
//                   }));
//                 } else if (bookingid.length < 10) {
//                   setErrors((prev) => ({
//                     ...prev,
//                     bookingid: "Reservation number must be at least 10 digits.",
//                   }));
//                 }
//               }}
//               className={`w-full p-2.5 border ${
//                 errors.bookingid ? "border-red-500" : "border-gray-300"
//               } rounded-md placeholder-gray-400`}
//               placeholder="Enter your Reservation number"
//               required
//             />
//             <div className="h-2">
//               {errors.bookingid && (
//                 <p className="text-red-500 text-xs mt-1">{errors.bookingid}</p>
//               )}
//             </div>
//           </div>

//           <div className="mb-6">
//             <label
//               htmlFor="lastName"
//               className="block text-md font-medium text-black mb-1"
//             >
//               Last Name
//             </label>
//             <input
//               type="text"
//               id="lastName"
//               value={lastName}
//               onChange={handleLastNameChange}
//               onBlur={() => {
//                 if (!lastName.trim()) {
//                   setErrors((prev) => ({
//                     ...prev,
//                     lastName: "Last name is required.",
//                   }));
//                 }
//               }}
//               className={`w-full p-2.5 border ${
//                 errors.lastName ? "border-red-500" : "border-gray-300"
//               } rounded-md placeholder-gray-400`}
//               placeholder="Enter your Last name"
//               required
//             />
//             <div className="h-2">
//               {errors.lastName && (
//                 <p className="text-red-500 text-xs mt-1">{errors.lastName}</p>
//               )}
//             </div>
//           </div>

//           <button
//             type="submit"
//             className="w-full py-2.5 font-poppins bg-black text-white rounded-md hover:bg-gray-800 font-semibold text-base transition-colors"
//             disabled={isLoading}
//           >
//             {isLoading ? "Searching..." : "Search Booking"}
//           </button>
//         </form>
//         {showPopup && (
//           <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm">
//             <div className="relative bg-white rounded-lg shadow-lg max-w-[550px] w-full p-6 mx-4">
//               <button
//                 onClick={() => setShowPopup(false)}
//                 className="absolute top-4 right-4 text-gray-500 hover:text-gray-700"
//               >
//                 <svg
//                   xmlns="http://www.w3.org/2000/svg"
//                   className="h-6 w-6"
//                   fill="none"
//                   viewBox="0 0 24 24"
//                   stroke="currentColor"
//                 >
//                   <path
//                     strokeLinecap="round"
//                     strokeLinejoin="round"
//                     strokeWidth={2}
//                     d="M6 18L18 6M6 6l12 12"
//                   />
//                 </svg>
//               </button>

//               <div className="text-center pt-2">
//                 <BsFillExclamationTriangleFill className="text-yellow-400 text-4xl mx-auto mb-3" />
//                 <h2 className="text-gray-900 text-lg font-semibold mb-2">
//                   Unable to Proceed
//                 </h2>
//                 <p className="text-red-600 text-md font-medium mb-3 whitespace-pre-line">
//                   {popupMessage}
//                 </p>
//                 <p className="text-gray-700 text-sm mb-4 mt-2">
//                   Please check your Reservation No and Last Name. In case you
//                   are not able to search your reservation please contact our
//                   reception asistance support team at{" "}
//                   <a
//                     href={`mailto:${hotelEmail}`}
//                     className="text-blue-600 hover:underline"
//                   >
//                     {hotelEmail}
//                   </a>{" "}
//                   or call us at{" "}
//                   <a
//                     href={`tel:${phone}`}
//                     className="text-blue-600 hover:underline"
//                   >
//                     {phone}
//                   </a>
//                 </p>

//                 <button
//                   onClick={() => setShowPopup(false)}
//                   className="px-5 py-2 bg-black text-white rounded-md font-medium"
//                 >
//                   Try Again
//                 </button>
//               </div>
//             </div>
//           </div>
//         )}
//       </div>
//     </div>
//   );
// };

// export default Booking;
